import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Users, Search, Plus, Music, Lock, Globe, 
  Calendar, MessageSquare, UserPlus, ChevronRight,
  MapPin, UserMinus, Shield, Edit2, Save, X,
  ChevronDown
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Button from '../components/Button';
import Card from '../components/Card';
import { format } from 'date-fns';
import UserName from '../components/UserName';
import CreatePostModal from '../components/CreatePostModal';
import CreateEventModal from '../components/CreateEventModal';
import PostDetailsModal from '../components/PostDetailsModal';
import PostCard from '../components/PostCard';
import EventCard from '../components/EventCard';
import CommunityImageUpload from '../components/CommunityImageUpload';
import MembershipRequestsModal from '../components/MembershipRequestsModal';
import type { Community, CommunityMember, CommunityPost, CommunityEvent, MembershipRequest } from '../types';
import supabase from '../lib/supabase';
import Avatar from './Avatar';

const genreOptions = [
  'None',
  'Acoustic',
  'Acoustic Guitar',
  'Adult Alternative',
  'Adult Contemporary',
  'Adult Urban Contemporary',
  'African Music',
  'Afrikaans',
  'Afro-Beat',
  'Afro-Pop',
  'Alt Country',
  'Alt Rock',
  'Alternative',
  'Alternative Metal',
  'Alternativo en Español',
  'Ambient',
  'Americana',
  'Anime',
  'Arabesque',
  'Arabic',
  'Arabic Pop',
  'Arena Rock',
  'Aussie Hip-Hop/Rap',
  'Australian',
  'Austrian',
  'Avant-Garde Jazz',
  'Axé',
  'Bachata',
  'Baile Funk',
  'Bakersfield Sound',
  'Banda',
  'Baroque Era',
  'Bashment',
  'Bass',
  'Bebop',
  'Belgian',
  'Big Band',
  'Black Metal',
  'Blackgaze',
  'Bluegrass',
  'Blues',
  'Boleros',
  'Bollywood',
  'Bossa Nova',
  'Brazilian',
  'Breakbeat',
  'British',
  'British Invasion',
  'British Metal',
  'Britpop',
  'C-Pop',
  'Calypso',
  'Canadian',
  'Cantautori',
  'Cantopop',
  'Caribbean',
  'Celtic',
  'Chamber Music',
  'Chicago Blues',
  'Chillwave',
  'Chinese',
  'Chinese Alt',
  'Chinese Hip-Hop',
  'Chinese Rock',
  'Choral',
  'Christian',
  'Christian Country',
  'Christian Pop',
  'Christian Rap',
  'Christian Rock',
  'Classic Blues',
  'Classic Country',
  'Classic Metal',
  'Classic Rock',
  'Classic Singer/Songwriter',
  'Classical',
  'Classical Guitar',
  'Coffee Shop',
  'College Rock',
  'Comedy',
  'Contemporary Blues',
  'Contemporary Classical',
  'Contemporary Gospel',
  'Contemporary Jazz',
  'Corridos',
  'Country Rock',
  'Country',
  'Countrypolitan',
  'Crunk',
  'Cuban',
  'Cubaton',
  'Cumbia',
  'Dance',
  'Dancehall',
  'Dangdut',
  'Danish',
  'DarkWave',
  'Death Metal',
  'Deathcore',
  'Deep House',
  'Delta Blues',
  'Deutscher Streetrap',
  'Deutschpop',
  'Devotional & Spiritual',
  'Dirty South',
  'Disco',
  'Disney',
  'Dixieland',
  'Doo Wop',
  'Doom',
  'Downtempo',
  'Dub',
  'Dubstep',
  'Duranguense',
  'Dutch',
  'Dutch Pop',
  'East Coast',
  'Easy Listening',
  'Egyptian Pop',
  'Electric Blues',
  'Electro',
  'Electronic',
  'Electronica',
  'EMO',
  'Enka',
  'Experimental',
  'Extreme Metal',
  'Fado',
  'Farsi',
  'Filipino',
  'Finnish',
  'Flamenco',
  'Folk',
  'Folk-Rock',
  'Forró',
  'Freestyle',
  'French',
  'French Hip-Hop/Rap',
  'French Pop',
  'French Variété',
  'Funk',
  'Fusion Jazz',
  'Gangsta Rap',
  'Garage',
  'German',
  'German Folk',
  'Ghazals',
  'Glam Rock',
  'Golden Age',
  'Gospel',
  'Goth',
  'Grime',
  'Grindcore',
  'Grunge',
  'Hair Metal',
  'Halk',
  'Hanukkah',
  'Hard Bop',
  'Hard Rock',
  'Hardcore Punk',
  'Hawaiian',
  'Hindi',
  'Hip-Hop en Espańol',
  'Hip-Hop/Rap',
  'Hip-Hop',
  'Hip-Hop/Rap Italiano',
  'Holiday',
  'Honky Tonk',
  'Hörspiele',
  'House',
  'Hyphy',
  'IDM',
  'Indian',
  'Indian Classical',
  'Indian Folk',
  'Indian Pop',
  'Indie',
  'Indie Dance',
  'Indie en Espańol',
  'Indie Pop',
  'Indie R&B',
  'Indie Rock',
  'Indonesian Music',
  'Industrial Metal',
  'Inspirational',
  'Instrumental',
  'Irish',
  'Israeli',
  'Israeli Alternative',
  'Israeli Pop',
  'Israeli Rock',
  'Italian',
  'J-Hip-Hop',
  'J-Pop',
  'J-R&B',
  'J-Rock',
  'Jam Bands',
  'Japanese',
  'Jazz',
  'Jazz Vocal',
  'Jungle',
  'K Hip-Hop',
  'K Rap',
  'K-Indie',
  'K-Pop',
  'K-R&B',
  'K-Rock',
  'Kannada',
  'Karaoke',
  'Kayokyoku',
  'Khaleeji',
  'Khaleeji Pop',
  'Khaleeji Tarab',
  'Kids & Family',
  'Korean',
  'Kwanzaa',
  'Latin Jazz',
  'Latino',
  'Levant Pop',
  'Lounge',
  'Lovers Rock',
  'Lullabies',
  'Malayalam',
  'Malaysian Music',
  'Malaysian Pop',
  'Mandopop',
  'Marathi',
  'Mariachi',
  'Memphis Soul',
  'Merengue',
  'Metal',
  'Metalcore',
  'Mexican',
  'Minimalism',
  'Mizrahi',
  'Mod',
  'Modern Dancehall',
  'Modern Era',
  'Motown',
  'MPB',
  'Música Mexicana',
  'Música Religios',
  'Musicals',
  'Musique Francophone',
  'Muzak',
  'Nature',
  'Neo-Soul',
  'New Age',
  'New Jack Swing',
  'New Romantic',
  'New Wave',
  'Norteño',
  'Norwegian',
  'Nu Metal',
  'Nueva Canción',
  'Nursery Rhymes',
  'Old School',
  'Oldies',
  'Opera',
  'Orchestral',
  'Outlaw Country',
  'Pagode',
  'Piano',
  'Pinoy Pop',
  'Polka',
  'Pop',
  'Pop in Portuguese',
  'Pop Italiano',
  'Pop Latino',
  'Pop Punk',
  'Pop Rock',
  'Portugeuse',
  'Post-Grunge',
  'Post-Punk',
  'Post-Rock',
  'Power Pop',
  'Praise & Worship',
  'Prog-Rock',
  'Progressive House',
  'Psych Rock',
  'Punjabi',
  'Punk',
  'Quebec',
  'Quiet Storm',
  'R&B',
  'Ragtime',
  'Rancheras',
  'Rap',
  'Rave',
  'Reggae',
  'Reggaeton',
  'Regional Indian',
  'Relaxation',
  'Renaissance',
  'Requinto',
  'Rock',
  'Rock in Portuguese',
  'Rock en Español',
  'Rockabilly',
  'Romantic Era',
  'Roots Reggae',
  'Roots Rock',
  'Russian',
  'Russian Alternative',
  'Russian Chanson',
  'Russian Hip-Hop',
  'Russian Pop',
  'Russian Rock',
  'Salsa',
  'Samba',
  'Sanat',
  'Saxophone',
  'Schlager',
  'Scores',
  'Scottish',
  'Sertanejo',
  'Shoegaze',
  'Singer/Songwriter',
  'Ska',
  'Sludge',
  'Smooth Jazz',
  'Soca',
  'Soft Rock',
  'Son Jarocho',
  'Sonidero',
  'Soul',
  'Soul Jazz',
  'Southern Gospel',
  'Southern Rock',
  'South American',
  'Spanish',
  'Stage and Screen',
  'Standards',
  'Stoner Rock',
  'Strings',
  'Sufi & Ghazals',
  'Surf',
  'Swiss',
  'Synth-Pop',
  'Tai-Pop',
  'Tamil',
  'Tango',
  'Tarab',
  'Techno',
  'Teen Pop',
  'Telugu',
  'Thai Music',
  'Thai Pop',
  'Third Wave Ska',
  'Thrash Metal',
  'Tololoche',
  'Traditional Bluegrass',
  'Traditional Country',
  'Traditional Folk',
  'Trance',
  'Trap',
  'Trap Latino',
  'Tropical House',
  'Trova',
  'Turkish',
  'UK Garage',
  'UK Hip-Hop/Rap',
  'Underground Hip-Hop/Rap',
  'Urbano Latino',
  'Vallenato',
  'Vietnamese',
  'West Coast',
  'Worldbeat',
  'Yacht Rock',
  'Christmas',
  'Halloween',
  "Children's Music",
  'Fitness & Workout',
  'Soundtrack',
  'Spoken Word',
  'Vocal',
  'Christian & Gospel',
  'Música Tropical',
  'Rock Y Alternativo',
  'Soul/Funk',
  'World'
];

export default function CommunityDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [community, setCommunity] = React.useState<Community | null>(null);
  const [members, setMembers] = React.useState<CommunityMember[]>([]);
  const [posts, setPosts] = React.useState<CommunityPost[]>([]);
  const [events, setEvents] = React.useState<CommunityEvent[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [isJoining, setIsJoining] = React.useState(false);
  const [isLeaving, setIsLeaving] = React.useState(false);
  const [showCreatePost, setShowCreatePost] = React.useState(false);
  const [showCreateEvent, setShowCreateEvent] = React.useState(false);
  const [showMemberList, setShowMemberList] = React.useState(false);
  const [selectedPost, setSelectedPost] = React.useState<CommunityPost | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isEditing, setIsEditing] = React.useState(false);
  const [isGenreDropdownOpen, setIsGenreDropdownOpen] = React.useState(false);
  const [editForm, setEditForm] = React.useState({
    name: '',
    description: '',
    genres: [] as string[],
    location: '',
    location_enabled: false,
    is_private: false
  });
  const memberListRef = React.useRef<HTMLDivElement>(null);
  const [hasPendingRequest, setHasPendingRequest] = React.useState(false);
  const [showMembershipRequests, setShowMembershipRequests] = React.useState(false);
  const [membershipRequests, setMembershipRequests] = React.useState<MembershipRequest[]>([]);
  const [pendingRequestsCount, setPendingRequestsCount] = React.useState(0);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  // Helper function to check if URL is from Supabase storage
  const isSupabaseStorageUrl = (url: string): boolean => {
    return url.includes('supabase') && url.includes('/storage/');
  };

  // Helper function to add cache busting to Supabase URLs
  const addCacheBusting = (url: string): string => {
    if (!url) return url;
    
    if (isSupabaseStorageUrl(url)) {
      // Remove existing cache busting params and add new one
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?v=${Date.now()}`;
    }
    
    return url;
  };

  const isMember = React.useMemo(() => {
    return members.some(member => member.user_id === user?.id);
  }, [members, user?.id]);

  const isLeader = React.useMemo(() => {
    return community?.leader_id === user?.id;
  }, [community?.leader_id, user?.id]);

  const canViewContent = React.useMemo(() => {
    // Can view content if:
    // 1. Community is not private, or
    // 2. User is a member, or
    // 3. User is the leader
    return !community?.is_private || isMember || isLeader;
  }, [community?.is_private, isMember, isLeader]);

  React.useEffect(() => {
    if (community) {
      setEditForm({
        name: community.name,
        description: community.description,
        genres: community.genres || [],
        location: community.location || '',
        location_enabled: community.location_enabled || false,
        is_private: community.is_private
      });
    }
  }, [community]);

  // Check if user has a pending membership request
  React.useEffect(() => {
    if (!user?.id || !id || !community?.is_private) return;
    
    const checkPendingRequest = async () => {
      try {
        const { data, error } = await supabase
          .from('membership_requests')
          .select('status')
          .eq('community_id', id)
          .eq('user_id', user.id)
          .eq('status', 'pending')
          .maybeSingle();
        
        if (error) throw error;
        setHasPendingRequest(!!data);
      } catch (err) {
        console.error('Error checking pending request:', err);
      }
    };
    
    checkPendingRequest();
  }, [id, user?.id, community?.is_private]);

  // Fetch membership requests for community leaders
  React.useEffect(() => {
    const fetchMembershipRequests = async () => {
      if (!id || !isLeader) return;
      
      try {
        // Query to join user profiles
        const { data, error } = await supabase
          .from('membership_requests')
          .select(`
            id,
            community_id,
            user_id,
            message,
            status,
            created_at,
            updated_at,
            user:user_profiles(
              id,
              full_name,
              display_name,
              verified,
              avatar_url
            )
          `)
          .eq('community_id', id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching membership requests:', error);
          throw error;
        }
        
        setMembershipRequests(data || []);
        setPendingRequestsCount(data?.length || 0);
      } catch (err) {
        console.error('Error fetching membership requests:', err);
      }
    };
    
    fetchMembershipRequests();
  }, [id, isLeader]);

  const handleGenreToggle = (genre: string) => {
    setEditForm(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handleSaveChanges = async () => {
    if (!id) return;

    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('communities')
        .update({
          name: editForm.name,
          description: editForm.description,
          genres: editForm.genres,
          location: editForm.location_enabled ? editForm.location : null,
          location_enabled: editForm.location_enabled,
          is_private: editForm.is_private,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setCommunity(prev => prev ? {
        ...prev,
        ...editForm,
        location: editForm.location_enabled ? editForm.location : null
      } : null);

      setIsEditing(false);
    } catch (error: any) {
      console.error('Error updating community:', error);
      setError(error.message);
    }
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('community_categories')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  const fetchPosts = React.useCallback(async () => {
    if (!id || !canViewContent) return;

    try {
      let postsQuery = supabase
        .from('community_posts')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          category:category_id(
            id,
            name
          ),
          likes
        `)
        .eq('community_id', id)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'all') {
        postsQuery = postsQuery.eq('category_id', selectedCategory);
      }

      const { data: postData, error: postError } = await postsQuery;

      if (postError) throw postError;
      setPosts(postData || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts');
    }
  }, [id, selectedCategory, canViewContent]);

  const fetchCommunityDetails = React.useCallback(async () => {
    if (!id) return;

    try {
      setError(null);

      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select(`
          *,
          leader:leader_id(
            id,
            full_name,
            display_name,
            verified
          )
        `)
        .eq('id', id)
        .single();

      if (communityError) throw communityError;

      // Add cache busting to cover image if it exists
      if (communityData.cover_image) {
        communityData.cover_image = addCacheBusting(communityData.cover_image);
      }

      const { data: memberData, error: memberError } = await supabase
        .from('community_members')
        .select(`
          *,
          user:user_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('community_id', id)
        .order('role', { ascending: false })
        .order('joined_at', { ascending: true });

      if (memberError) throw memberError;

      // Only fetch events if user can view content
      if (communityData && (!communityData.is_private || 
          communityData.leader_id === user?.id || 
          memberData?.some(member => member.user_id === user?.id))) {
        
        const { data: eventData, error: eventError } = await supabase
          .from('community_events')
          .select(`
            *,
            creator:creator_id(
              id,
              full_name,
              display_name,
              verified,
              avatar_url
            ),
            rsvps:event_rsvps(count)
          `)
          .eq('community_id', id)
          .gte('start_time', new Date().toISOString())
          .order('start_time', { ascending: true })
          .limit(5);

        if (eventError) throw eventError;
        setEvents(eventData || []);
      } else {
        // Clear events if user can't view them
        setEvents([]);
      }

      setCommunity(communityData);
      setMembers(memberData || []);

      await fetchPosts();
    } catch (err: any) {
      console.error('Error fetching community details:', err);
      setError(err.message);
    }
  }, [id, fetchPosts, user?.id]);

  React.useEffect(() => {
    fetchCommunityDetails();
  }, [fetchCommunityDetails]);

  const handleJoinCommunity = async () => {
    if (!user || !id) return;

    try {
      setIsJoining(true);
      setError(null);

      const { data: community } = await supabase
        .from('communities')
        .select('is_private')
        .eq('id', id)
        .single();

      if (!community) throw new Error('Community not found');

      if (!community.is_private) {
        const { error: joinError } = await supabase
          .from('community_members')
          .insert({
            community_id: id,
            user_id: user.id,
            role: 'member'
          });

        if (joinError) throw joinError;

        await fetchCommunityDetails();
      } else {
        const { error: requestError } = await supabase
          .from('membership_requests')
          .insert({
            community_id: id,
            user_id: user.id,
            status: 'pending'
          });

        if (requestError) throw requestError;
        
        // Update local state to show request is pending
        setHasPendingRequest(true);
      }
    } catch (error: any) {
      console.error('Error joining community:', error);
      setError(error.message);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveCommunity = async () => {
    if (!user || isLeader || !id) return;

    try {
      setIsLeaving(true);
      setError(null);

      const { error: leaveError } = await supabase
        .from('community_members')
        .delete()
        .eq('community_id', id)
        .eq('user_id', user.id);

      if (leaveError) throw leaveError;

      await fetchCommunityDetails();
    } catch (error: any) {
      console.error('Error leaving community:', error);
      setError(error.message);
    } finally {
      setIsLeaving(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!id) return;
    
    try {
      const { error } = await supabase
        .from('community_members')
        .delete()
        .eq('community_id', id)
        .eq('user_id', userId);

      if (error) throw error;

      await fetchCommunityDetails();
    } catch (error: any) {
      console.error('Error removing member:', error);
      setError(error.message);
    }
    };

  const handleToggleLeader = async (userId: string, makeLeader: boolean) => {
    if (!id) return;
    
    try {
      if (makeLeader) {
        const { error: updateError } = await supabase
          .from('communities')
          .update({ leader_id: userId })
          .eq('id', id);

        if (updateError) throw updateError;
      }

      await fetchCommunityDetails();
    } catch (error: any) {
      console.error('Error updating leader:', error);
      setError(error.message);
    }
  };

  const handlePostUpdate = async () => {
    await fetchPosts();
  };

  const handleCoverImageUpdate = async (url: string) => {
    if (!id) return;

    try {
      // The URL from CommunityImageUpload already has cache busting
      const { error: updateError } = await supabase
        .from('communities')
        .update({ 
          cover_image: url,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update the local state with the cache-busted URL
      setCommunity(prev => prev ? { ...prev, cover_image: url } : null);
    } catch (error: any) {
      console.error('Error updating cover image:', error);
      setError(error.message);
    }
  };

  const handleMembershipRequestAction = async (requestId: string, action: 'approve' | 'reject') => {
    if (!id || !isLeader) return;
    
    try {
      if (action === 'approve') {
        // First check if the user is already a member to avoid duplicate key error
        const { data: request, error: fetchError } = await supabase
          .from('membership_requests')
          .select('user_id')
          .eq('id', requestId)
          .single();
        
        if (fetchError) throw fetchError;
        
        // Check if user is already a member
        const { data: existingMember, error: memberCheckError } = await supabase
          .from('community_members')
          .select('user_id')
          .eq('community_id', id)
          .eq('user_id', request.user_id)
          .maybeSingle();
          
        if (memberCheckError) throw memberCheckError;
        
        // Only add as member if not already a member
        if (!existingMember) {
          // Add user as a member
          const { error: memberError } = await supabase
            .from('community_members')
            .insert({
              community_id: id,
              user_id: request.user_id,
              role: 'member'
            });
          
          if (memberError) throw memberError;
        }
        
        // Update request status regardless
        const { error: updateError } = await supabase
          .from('membership_requests')
          .update({ 
            status: 'approved',
            updated_at: new Date().toISOString()
          })
          .eq('id', requestId);
        
        if (updateError) throw updateError;
      } else {
        // Reject the request
        const { error: updateError } = await supabase
          .from('membership_requests')
          .update({ 
            status: 'rejected',
            updated_at: new Date().toISOString()
          })
          .eq('id', requestId);
        
        if (updateError) throw updateError;
      }
      
      // Refresh membership requests
      const { data, error } = await supabase
        .from('membership_requests')
        .select(`
          id,
          community_id,
          user_id,
          message,
          status,
          created_at,
          updated_at,
          user:user_profiles(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('community_id', id)
        .eq('status', 'pending');
      
      if (error) throw error;
      setMembershipRequests(data || []);
      setPendingRequestsCount(data?.length || 0);
      
      // Refresh community details to update member list
      await fetchCommunityDetails();
    } catch (error: any) {
      console.error('Error handling membership request:', error);
      setError(error.message);
      throw error; // Re-throw to be caught by the modal
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberListRef.current && !memberListRef.current.contains(event.target as Node)) {
        setShowMemberList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!community) {
    return null;
  }

  return (
   <>
    <div className="p-4 pb-20 bg-neutral">
      <Card hover={false}>
        {isEditing && (
            <div className="sticky top-0 bg-neutral border-b border-neutral p-4 flex items-start justify-between shadow-md z-10 rounded-t-xl mb-8">
              <h2 className="text-3xl font-bold font-dillan text-secondary">
                Edit Community
              </h2>
            </div>
        )}
        <div className="relative">
          <div className="w-full h-32 bg-primary bg-opacity-10 rounded-t-xl flex items-center justify-center">
            {isEditing ? (
              <CommunityImageUpload
                initialImage={community?.cover_image}
                communityId={id!}
                onUploadComplete={handleCoverImageUpdate}
                className="w-full h-32 rounded-t-xl object-cover"
              />
            ) : community?.cover_image ? (
              <img
                src={community.cover_image}
                alt={community.name}
                className="w-full h-32 rounded-t-xl object-cover"
                key={community.cover_image} // Force re-render when URL changes
              />
            ) : (
              <Users className="w-12 h-12 text-primary opacity-20" />
            )}
          </div>
          <div className="absolute top-2 right-2">
            {community?.is_private ? (
              <div className="bg-primary backdrop-blur-sm text-surface-light rounded-full px-3 py-1 text-sm font-medium flex items-center">
                <Lock className="w-4 h-4 mr-1.5" />
                Private
              </div>
            ) : (
              <div className="bg-primary bg-opacity-90 backdrop-blur-sm text-surface-light rounded-full px-3 py-1 text-sm font-medium flex items-center">
                <Globe className="w-4 h-4 mr-1.5" />
                Public
              </div>
            )}
          </div>
        </div>

        <div className="p-0 mt-4">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-secondary border border-secondary rounded-full text-primary focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-primary font-dillan"
                  placeholder="Enter community name"
                />
              </div>

              <div>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 pl-4 bg-secondary text-primary rounded-full focus:outline-none focus:ring-0 focus:ring-primary resize-none placeholder:text-primary"
                  placeholder="Describe your community (100-300 characters)"
                />
              </div>

              <div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsGenreDropdownOpen(!isGenreDropdownOpen)}
                    className="w-full px-3 py-2 text-primary bg-secondary border border-secondary rounded-full focus:outline-none focus:ring-0 focus:ring-primary text-left flex justify-between items-center"
                  >
                    <span>
                      {editForm.genres.length === 0 
                        ? 'Select genre(s)' 
                        : `${editForm.genres.length} genre${editForm.genres.length === 1 ? '' : 's'} selected`
                      }
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isGenreDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isGenreDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-secondary border border-seconday rounded-lg shadow-lg max-h-60 overflow-y-auto text-primary font-dillan">
                      {genreOptions.map((genre) => (
                        <label
                          key={genre}
                          className="flex items-center px-3 py-2 hover:bg-primary hover:text-secondary cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={editForm.genres.includes(genre)}
                            onChange={() => handleGenreToggle(genre)}
                            className="w-2 h-2 border-2 border-primary rounded-full 
                appearance-none transition-all 
                checked:bg-primary checked:border-primary
                hover:border-secondary
                hover:checked:bg-secondary hover:checked:border-secondary"
                          />
                          <span className="text-sm pl-2">{genre}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {editForm.genres.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 pl-2">
                      {editForm.genres.map((genre) => (
                        <span
                          key={genre}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-surface-light"
                        >
                          {genre}
                          <button
                            type="button"
                            onClick={() => handleGenreToggle(genre)}
                            className="ml-1 hover:text-surface-light"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 pl-2">
                    <input
                      type="checkbox"
                      checked={editForm.location_enabled}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location_enabled: e.target.checked }))}
                      className="w-4 h-4 pl-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary"
                    />
                    <span className="ml-2 text-sm text-primary">
                      Enable location for this community
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 bg-secondary text-primary placeholder:text-primary border border-secondary rounded-full focus:outline-none focus:ring-0 focus:ring-primary"
                      placeholder="e.g. London, United Kingdom"
                    />
                  </div>

                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 pl-2">
                  <input
                    type="checkbox"
                    checked={editForm.is_private}
                    onChange={(e) => setEditForm(prev => ({ ...prev, is_private: e.target.checked }))}
                    className="w-4 h-4 pl-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary"
                  />
                  <span className="text-sm text-primary">
                    Make this community private
                  </span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                  onClick={handleSaveChanges}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <div className="flex-1">
                  <h1 className="font-dillan text-2xl font-bold text-primary">
                    {community?.name}
                  </h1>
                </div>
                <div className="relative">
                  <button
                    onMouseEnter={() => setShowMemberList(true)}
                    className="flex items-center text-primary hover:text-accent transition-colors"
                  >
                    <Users className="w-4 h-4 mr-1.5" />
                    {members.length} members
                  </button>

                  {showMemberList && (
                    <div 
                      ref={memberListRef}
                      className="absolute right-0 top-full mt-2 bg-primary-dark rounded-xl shadow-elevation-2 p-4 min-w-[200px] max-h-[600px] overflow-y-auto z-50"
                      onMouseLeave={() => setShowMemberList(false)}
                    >
                      <div className="space-y-3">
                        {members.map((member) => (
                          <div
                            key={member.user_id}
                            className="group flex items-center space-x-2 text-surface-light hover:bg-neutral hover:text-neutral p-2 rounded-lg transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <Avatar
                                src={member.user.avatar_url || `https://ui-avatars.com/api/?name=${member.user.display_name || member.user.full_name}`}
                                alt={member.user.display_name || member.user.full_name}
                                size="sm"
                              />
                              <div>
                                <UserName
                                  name={member.user.display_name || member.user.full_name}
                                  verified={member.user.verified}
                                  size="sm"
                                  userId={member.user.id}
                                />
                                {member.role === 'leader' && (
                                  <div className="flex items-center text-xs text-surface-light">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Community Leader
                                  </div>
                                )}
                              </div>
                            </div>

                            {isLeader && member.role !== 'leader' && (
                              <div className="flex items-center pl-8">
                                <button
                                  onClick={() => handleToggleLeader(member.user_id, true)}
                                  className="w-20 text-xs text-surface-light group-hover:text-surface-light hover:!text-primary"
                                >
                                  Make Leader
                                </button>
                                <span className="group-hover:text-surface-light">/</span>
                                <button
                                  onClick={() => handleRemoveMember(member.user_id)}
                                  className="pl-2 text-xs text-surface-light group-hover:text-surface-light hover:!text-primary"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {community?.genres && community.genres.length > 0 && (
             <div className="w-full text-xs italic font-medium text-neutral mb-4">
                {community.genres.slice(0, 3).join(", ")}
                {community.genres.length > 3 && `, +${community.genres.length - 3} more`}
              </div>
              )}

              <p className="text-primary text-sm mb-4 break-words whitespace-pre-line">{community?.description}</p>

              {community?.location && community.location_enabled && (
                <div className="flex items-center text-neutral text-sm mb-4">
                  <MapPin className="w-4 h-4 mr-1.5" />
                  {community.location}
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-neutral font-medium mb-4">
                {community.leader && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-neutral mb-1">Led by</div>
                    <UserName
                      name={community.leader.display_name || community.leader.full_name}
                      verified={community.leader.verified}
                      size="sm"
                      userId={community.leader.id}
                    />
                  </div>
                )}
                <div className="mb-4 text-primary">
                    <div className="text-xs font-medium text-neutral mb-1">Created</div>
                  {format(new Date(community.created_at), 'MMM yyyy')}
                </div>
              </div>
              
              {isLeader && (
                <div className="flex items-center justify-between mb-4 gap-2">
                  {community?.is_private && pendingRequestsCount >= 0 && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="text-xs h-10 px-4"
                      onClick={() => setShowMembershipRequests(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Membership Requests ({pendingRequestsCount})
                    </Button>
                  )}
                  <div className="flex-grow"></div>
                  {!isEditing && (
                    <Button
                      onClick={() => setIsEditing(true)}
                      className="w-12 p-0 text-neutral hover:text-surface-light transition-colors border-0 border-surface-light hover:border-surface-light"
                      title="Edit Community"
                    >
                      <Edit2 className="w-5 h-5 text-primary" />
                    </Button>
                  )}
                </div>
              )}


              {user && !isLeader && (
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={isMember ? handleLeaveCommunity : handleJoinCommunity}
                  disabled={isJoining || isLeaving || hasPendingRequest}
                >
                  {isMember ? (
                    <>
                      <UserMinus className="w-4 h-4 mr-1.5" />
                      {isLeaving ? 'Leaving...' : 'Leave Community'}
                    </>
                  ) : hasPendingRequest ? (
                    <>
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Request Pending
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      {isJoining ? 'Requesting...' : 
                       community.is_private ? 'Request to Join' : 'Join Community'}
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </Card>
      <div className="h-4" />
      {/* Only show discussions and events if user can view content */}
      {canViewContent ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-2xl font-bold font-dillan text-primary">
                  Discussions
                </h2>
                {isMember && (
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="w-32 px-1 py-0.5 h-12 text-sm bg-neutral text-surface-light hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                    onClick={() => setShowCreatePost(true)}
                  >
                    <Plus className="w-4 h-4 mr-0" />
                    New Post
                  </Button>
                )}
              </div>

              <div className="flex justify-end mb-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-32 px-3 py-2 pr-0 rounded-full bg-surface-light text-neutral font-dillan focus:outline-none transition-colors truncate overflow-hidden whitespace-nowrap text-sm"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {posts.length > 0 ? (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      onReplyClick={() => setSelectedPost(post)}
                      onUpdate={handlePostUpdate}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-neutral mx-auto mb-2 opacity-50" />
                  <p className="text-neutral">No discussions yet</p>
                  {isMember && (
                    <p className="text-sm text-neutral mt-1">
                      Start a conversation with your community
                    </p>
                  )}
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold font-dillan text-primary">
                  Events
                </h2>
                {isMember && (
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="w-32 px-1 py-0.5 h-12 text-sm bg-neutral text-surface-light hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                    onClick={() => setShowCreateEvent(true)}
                  >
                    <Plus className="w-4 h-4 mr-0" />
                    New Event
                  </Button>
                )}
              </div>

              {events.length > 0 ? (
                <div className="space-y-4 gap-6">
                  {events.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onUpdate={fetchCommunityDetails}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-neutral mx-auto mb-2 opacity-50" />
                  <p className="text-neutral">No upcoming events</p>
                  {isMember && (
                    <p className="text-sm text-neutral mt-1">
                      Create an event for your community
                    </p>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      ) : (
        <div className="bg-surface-light rounded-xl p-6 text-center">
          <Lock className="w-12 h-12 text-neutral mx-auto mb-4 opacity-50" />
          <h2 className="text-xl font-bold font-dillan text-primary mb-2">
            This is a private community
          </h2>
          <p className="text-neutral mb-6">
            You need to be a member to view discussions and events.
          </p>
          
        </div>
      )}

      {showCreatePost && (
        <CreatePostModal
          communityId={id!}
          onClose={() => setShowCreatePost(false)}
          onSuccess={() => {
            setShowCreatePost(false);
            fetchPosts();
          }}
        />
      )}

      {showCreateEvent && (
        <CreateEventModal
          communityId={id!}
          onClose={() => setShowCreateEvent(false)}
          onSuccess ={() => {
            setShowCreateEvent(false);
            fetchCommunityDetails();
          }}
        />
      )}

      {selectedPost && (
        <PostDetailsModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onUpdate={handlePostUpdate}
        />
      )}
    </div>
    {showMembershipRequests && community?.is_private && (
      <MembershipRequestsModal
        requests={membershipRequests}
        onClose={() => setShowMembershipRequests(false)}
        onAction={handleMembershipRequestAction}
      />
    )}
    </> 
  );
}